/**
 * Performance tracking utility to help identify and debug LCP issues
 */

// Enable only in development mode or when debugging is enabled
const ENABLE_PERFORMANCE_TRACKING = process.env.NODE_ENV === 'development' || localStorage.getItem('debug_performance') === 'true';

/**
 * Measure and report LCP performance
 */
export function trackLCP() {
  if (!ENABLE_PERFORMANCE_TRACKING) return;
  
  // Only run if the Web Vitals API is available
  if ('PerformanceObserver' in window) {
    try {
      // Create LCP performance observer
      const lcpObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        if (entries.length > 0) {
          const lcpEntry = entries[entries.length - 1] as PerformanceEntry & {
            element?: Element;
            startTime: number;
          };
          
          console.group('🚀 LCP Performance Tracking');
          console.log('LCP Time:', Math.round(lcpEntry.startTime), 'ms');
          
          // Log the element that contributed to LCP
          if (lcpEntry.element) {
            console.log('LCP Element:', lcpEntry.element);
            console.log('LCP Element Type:', lcpEntry.element.tagName);
            console.log('LCP Element Text:', lcpEntry.element.textContent?.substring(0, 50) + '...');
            
            // Add visual marker in development
            if (process.env.NODE_ENV === 'development') {
              highlightLCPElement(lcpEntry.element);
            }
          } else {
            console.log('No LCP element detected');
          }
          
          // Check for common issues
          if (lcpEntry.startTime > 2500) {
            console.warn('⚠️ LCP is too slow (> 2.5s). Check for render-blocking resources.');
          }
          console.groupEnd();
        }
      });
      
      // Start observing LCP
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      
      return lcpObserver;
    } catch (error) {
      console.error('Failed to track LCP:', error);
    }
  }
  
  return null;
}

/**
 * Highlight the LCP element in development for debugging
 */
function highlightLCPElement(element: Element) {
  // Create a visual indicator by adding a border
  const originalStyles = element.getAttribute('style') || '';
  
  // Add highlight styles
  element.setAttribute(
    'style', 
    `${originalStyles}; outline: 3px solid red !important; position: relative;`
  );
  
  // Add label
  const label = document.createElement('div');
  label.textContent = 'LCP Element';
  label.style.cssText = `
    position: absolute;
    top: 0;
    left: 0;
    background: red;
    color: white;
    font-size: 10px;
    padding: 2px 5px;
    z-index: 9999;
    pointer-events: none;
  `;
  
  element.appendChild(label);
  
  // Remove highlight after 10 seconds
  setTimeout(() => {
    element.setAttribute('style', originalStyles);
    label.remove();
  }, 10000);
}

/**
 * Initialize performance tracking
 */
export function initPerformanceTracking() {
  // Track LCP
  const lcpObserver = trackLCP();
  
  // Return cleanup function
  return () => {
    lcpObserver?.disconnect();
  };
} 